import { Icon } from "@norican/icons";
import { Link } from "react-router";

import { HelpLayout, HelpSectionBlock } from "@/components/help-layout";
import { subText } from "@/components/table";
import { useMe } from "@/lib/api";
import { CHAPTERS } from "@/lib/help-chapters";

/**
 * The front page of the help centre: answers first, then the chapters.
 *
 * Anyone whose question is in the first list never reads further, which is
 * the point. The chapters are the long form, one per job.
 */
const ANSWERS: Array<{ q: string; a: string; to: string; linkLabel: string }> = [
  {
    q: "Somebody is leaving.",
    a: "Raise it, with the date and who takes the mailbox.",
    to: "/leaver",
    linkLabel: "New offboarding",
  },
  {
    q: "I named the wrong person, or they are staying.",
    a: "Open the record and revert or cancel it. Inside the fourteen-day window nothing irreversible has happened yet.",
    to: "/queue",
    linkLabel: "Find the record",
  },
  {
    q: "Somebody was offboarded and needs their access back.",
    a: "Open their record and choose Re-enable the account, with a reason. The account is enabled again in Entra and in on-prem AD.",
    to: "/queue",
    linkLabel: "Find the record",
  },
  {
    q: "I am worried about somebody who is still here.",
    a: "Raise a risk flag. Low and medium watch them; high restricts what they can do, and they will notice.",
    to: "/flags",
    linkLabel: "Risk flags",
  },
  {
    q: "A hire, an exit, a planned exit, a future position, or a change of supervisor, job, location or legal entity.",
    a: "Report it on the Workforce page. It goes to the approver; approval raises the offboarding, writes the org data to the directory, or files the new-user request for the IT helpdesk in Ivanti.",
    to: "/workforce",
    linkLabel: "Workforce",
  },
  {
    q: "Somebody needs to be on a list HR keeps.",
    a: "Add them on the access list. It is applied within a few minutes; remove them the same way, with a reason.",
    to: "/access",
    linkLabel: "Access lists",
  },
];

const SECTIONS = [
  { id: "answers", title: "What do you need to do?" },
  { id: "chapters", title: "Chapters" },
  { id: "wrong", title: "When something looks wrong" },
];

export function HelpPage() {
  const me = useMe().data;
  const chapters = CHAPTERS.filter((c) => c.to !== "/help" && (!c.show || c.show(me)));
  return (
    <HelpLayout
      title="Help"
      intro="HROps records who is leaving and when, disables the account in both directories at that time, keeps the mailbox reachable by the right colleague, and logs every step against the person who asked for it. Since October 2026 it also carries HR's workforce changes, with an approval."
      sections={SECTIONS}
    >
      <HelpSectionBlock id="answers" title="What do you need to do?">
        <dl className="space-y-3">
          {ANSWERS.map((item) => (
            <div key={item.q}>
              <dt className="text-fg font-medium">{item.q}</dt>
              <dd className={`${subText} mt-0.5`}>
                {item.a}{" "}
                <Link to={item.to} className="underline">
                  {item.linkLabel}
                </Link>
              </dd>
            </div>
          ))}
        </dl>
      </HelpSectionBlock>

      <HelpSectionBlock id="chapters" title="Chapters">
        <ul className="grid gap-3 sm:grid-cols-2">
          {chapters.map((c) => (
            <li key={c.to}>
              <Link
                to={c.to}
                className="border-border hover:bg-bg-subtle flex h-full gap-3 rounded border p-3"
              >
                <Icon icon={c.icon} size="md" className="text-fg-muted mt-0.5 shrink-0" />
                <span>
                  <span className="text-fg block font-medium">{c.label}</span>
                  <span className={subText}>{c.blurb}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </HelpSectionBlock>

      <HelpSectionBlock id="wrong" title="When something looks wrong">
        <ul className={`${subText} list-disc space-y-1 pl-5`}>
          <li>
            <span className="text-fg">A record sits at partial.</span> Half applied: the account may
            be disabled in one directory and live in the other. It is retried automatically; if it
            stays, tell SecOps.
          </li>
          <li>
            <span className="text-fg">Somebody must be out of everything now.</span> That is an
            incident. Call SecOps.
          </li>
          <li>
            <span className="text-fg">A menu or page is missing.</span> Access is a role in Entra.
            Sign out and back in once; if it is still missing, see{" "}
            <Link className="underline" to="/help/roles">
              Roles and access
            </Link>
            .
          </li>
        </ul>
      </HelpSectionBlock>
    </HelpLayout>
  );
}
